import React from 'react';
import { Text, Preset } from '@lux/text/tw';
import { cn, FunctionColor, SupportingColor } from '@lux/core';
import { CategoryTab } from '@shared/types/mylounge';
import { ChevronUpXS, ChevronDownXS } from '@lux/zds-icons';

import { ErrorBoundary } from '@browsing/components';
import { CategoryTabIds } from '@shared/constants';

import { useCrossCampaignEntryLargeScreen } from '../../cross-campaign/hooks/use-cross-campaign-entry-large-screen';
import { useGRE } from '../../gre';

type CrossCampaignEntryProps = ReturnType<typeof useCrossCampaignEntryLargeScreen>;

type TabProps = CrossCampaignEntryProps & {
    tab: CategoryTab;
    index: number;
    name: string;
    active: boolean;
    onAction: (index: number) => void;
};

export const Tab = ({
    tab,
    index,
    name,
    active,
    onAction,
    showMenu,
    hideMenu,
    isMenuShown,
    isExpandableTab,
    CrossCampaignEntryLargeScreen,
}: TabProps) => {
    const { checkIfGRETab } = useGRE();
    const isGRETab = checkIfGRETab(tab);

    const handleAction = () => {
        onAction(index);
    };

    const isMenuShownForTab = isMenuShown(tab.id as CategoryTabIds);

    return (
        <li>
            <div className="relative">
                <button
                    type="button"
                    role="link"
                    tabIndex={0}
                    onClick={handleAction}
                    data-testid={tab.id}
                    aria-current={active}
                    className={cn(
                        // Base styles
                        'mx-xs mb-[7px] mt-[10px]',
                        'text-center tracking-[0.03em]',
                        'cursor-pointer select-none whitespace-nowrap',
                        'bg-transparent p-0',
                        // Hover effect with transition
                        '[&_span]:transition-colors [&_span]:duration-100',
                        '[&_span]:hover:text-function-60',
                        // Active state
                        active && '[&_span]:hover:text-function-100 mt-[12px]',
                        // GRE + Active state
                        isGRETab && active && '[&_span]:hover:text-support-alert',
                    )}
                    style={
                        active
                            ? {
                                  borderBottom: `solid 2px ${isGRETab ? SupportingColor.alert : FunctionColor.function100}`,
                              }
                            : undefined
                    }
                    onMouseEnter={() => showMenu(tab.id as CategoryTabIds)}
                    onMouseLeave={() => hideMenu(250)}
                    onFocus={() => {
                        hideMenu(0);
                    }}
                >
                    <Text
                        preset={Preset.S}
                        bold
                        color={isGRETab ? SupportingColor.alert : FunctionColor.function100}
                    >
                        {name}
                    </Text>
                </button>
                {!isExpandableTab(tab.id as CategoryTabIds) ? null : (
                    <button
                        type="button"
                        tabIndex={0}
                        aria-label={name}
                        className={cn(
                            'absolute right-0 top-1/2 -translate-y-1/2 cursor-pointer',
                            '[&_svg_path]:fill-transparent',
                            'lux-focus-outline',
                            '[&:focus_svg_path]:fill-current',
                        )}
                        onClick={() => {
                            if (isMenuShownForTab) {
                                hideMenu(0);
                            } else {
                                showMenu(tab.id as CategoryTabIds);
                            }
                        }}
                        onFocus={() => hideMenu(0)}
                        onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => {
                            if ((event.shiftKey && event.key === 'Tab') || event.key === 'Escape') {
                                hideMenu(0);
                            }
                        }}
                        aria-expanded={isMenuShownForTab}
                    >
                        {isMenuShownForTab ? <ChevronUpXS /> : <ChevronDownXS />}
                    </button>
                )}
            </div>
            <ErrorBoundary componentName="CrossCampaignEntryLargeScreen">
                {isMenuShown(tab.id as CategoryTabIds) && <CrossCampaignEntryLargeScreen />}
            </ErrorBoundary>
        </li>
    );
};
